import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type { Comment, CommentEntityType } from '@/types'
import type { CommentsState } from './state'

export interface CreateCommentData {
  entityType: CommentEntityType
  entityId: string
  content: string
  isInternal?: boolean
  attachments?: string[]
}

export interface CreateGuestCommentData {
  entityType: CommentEntityType
  entityId: string
  content: string
  guestEmail: string
  guestName?: string
}

export interface UpdateCommentData {
  content?: string
  isInternal?: boolean
  attachments?: string[]
}

export const createActions = (state: CommentsState) => {
  const { logComment } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  const fetchComments = async (
    entityType: CommentEntityType,
    entityId: string,
    publicMode?: boolean
  ): Promise<void> => {
    state.isLoading = true
    state.error = null
    state.currentEntityType = entityType
    state.currentEntityId = entityId

    try {
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      const q = query(
        collection(db, 'comments'),
        ...baseConstraints,
        where('entityType', '==', entityType),
        where('entityId', '==', entityId),
        orderBy('createdAt', 'asc')
      )

      const snapshot = await getDocs(q)

      let comments = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          tenantId: data.tenantId,
          entityType: data.entityType,
          entityId: data.entityId,
          content: data.content,
          isInternal: data.isInternal || false,
          attachments: data.attachments || [],
          guestEmail: data.guestEmail || undefined,
          guestName: data.guestName || undefined,
          createdBy: data.createdBy || '',
          createdAt: data.createdAt?.toDate?.() ?? (data.createdAt ? new Date(data.createdAt) : new Date()),
          updatedAt: data.updatedAt?.toDate?.() ?? (data.updatedAt ? new Date(data.updatedAt) : new Date()),
          updatedBy: data.updatedBy || '',
        } as Comment
      })

      if (publicMode) {
        // Public mode: only show non-internal comments
        comments = comments.filter((c) => !c.isInternal)
      } else {
        // Authenticated mode: filter internal comments for non-admin users
        const authStore = useAuthStore()
        const isAdmin = authStore.user?.role === 'admin' || authStore.user?.role === 'operator'
        if (!isAdmin) {
          comments = comments.filter((c) => !c.isInternal)
        }
      }

      state.comments = comments
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching comments'
      console.error('Error fetching comments:', error)
    } finally {
      state.isLoading = false
    }
  }

  const createComment = async (data: CreateCommentData): Promise<string> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''

    const commentData = {
      ...getTenantData(),
      entityType: data.entityType,
      entityId: data.entityId,
      content: data.content,
      isInternal: data.isInternal || false,
      attachments: data.attachments || [],
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, 'comments'), commentData)

    const newComment: Comment = {
      id: docRef.id,
      entityType: data.entityType,
      entityId: data.entityId,
      content: data.content,
      isInternal: data.isInternal || false,
      attachments: data.attachments,
      createdBy: userId,
      updatedBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    state.comments.push(newComment)

    // Log activity
    await logComment(data.entityType, data.entityId)

    // Notify admins and operators about new comment
    await notificationsStore.notifyAdminsAndOperators(
      'comment_added',
      'Nuevo comentario',
      `Comentario en ${data.entityType}: "${data.content.substring(0, 80)}${data.content.length > 80 ? '...' : ''}"`,
      data.entityType as import('@/types').NotificationEntityType,
      data.entityId
    )

    return docRef.id
  }

  const createGuestComment = async (data: CreateGuestCommentData): Promise<string> => {
    const guestName = data.guestName || data.guestEmail.split('@')[0]

    const commentData = {
      ...getTenantData(),
      entityType: data.entityType,
      entityId: data.entityId,
      content: data.content,
      isInternal: false,
      attachments: [],
      createdBy: 'guest',
      updatedBy: 'guest',
      guestEmail: data.guestEmail,
      guestName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, 'comments'), commentData)

    const newComment: Comment = {
      id: docRef.id,
      entityType: data.entityType,
      entityId: data.entityId,
      content: data.content,
      isInternal: false,
      attachments: [],
      guestEmail: data.guestEmail,
      guestName,
      createdBy: 'guest',
      updatedBy: 'guest',
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    state.comments.push(newComment)

    return docRef.id
  }

  const updateComment = async (id: string, data: UpdateCommentData): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'comments', id)

    const updateData: Record<string, unknown> = {
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    if (data.content !== undefined) updateData.content = data.content
    if (data.isInternal !== undefined) updateData.isInternal = data.isInternal
    if (data.attachments !== undefined) updateData.attachments = data.attachments

    await updateDoc(docRef, updateData)

    const index = state.comments.findIndex((c) => c.id === id)
    if (index !== -1) {
      state.comments[index] = {
        ...state.comments[index],
        ...data,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }
  }

  const deleteComment = async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'comments', id))
    state.comments = state.comments.filter((c) => c.id !== id)
  }

  const clearComments = (): void => {
    state.comments = []
    state.currentEntityType = null
    state.currentEntityId = null
  }

  const getCommentById = (id: string): Comment | undefined => {
    return state.comments.find((c) => c.id === id)
  }

  return {
    fetchComments,
    createComment,
    createGuestComment,
    updateComment,
    deleteComment,
    clearComments,
    getCommentById,
  }
}
